import { refreshToken, refreshTokenB2C } from '@app/api'
import config, { vars } from '@app/config'
import { setCookie } from '@app/services'
import { getActiveB2CCookieNames, getAuthMode } from '@app/utils'
import { byteLength, MAX_TOKEN_BYTES, requestJwtFromNative } from '@app/utils'
import { hasFeature, WebViewFeature } from '@app/utils/webview/hasFeature'

import { logout } from './helpers'

type RefreshResult = {
  token: string | null
  refreshToken: string | null
} | null

type RetryQueueItem = {
  resolve: (value: RefreshResult | PromiseLike<RefreshResult>) => void
  reject: (error?: unknown) => void
}

let isRefreshing = false
let _refreshPromise: Promise<{
  token: string | null
  refreshToken: string | null
} | null> | null = null
let retryQueue: RetryQueueItem[] = []

const processQueue = (
  error: Error | null,
  token: string | null = null,
  refreshToken: string | null = null
) => {
  retryQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve({ token, refreshToken })
    }
  })
  retryQueue = []
}
const performTokenRefresh = async (
  doRefreshToken: string | boolean | null
): Promise<{ token: string | null; refreshToken: string | null } | null> => {
  try {
    const { isBackOfficeTokenMode } = getAuthMode()
    const { accessTokenCookie, refreshTokenCookie } = getActiveB2CCookieNames()

    const canDelegate =
      config.identity.isWebView &&
      hasFeature(WebViewFeature.DelegatedJwtExchange)

    let newToken: string | null = null
    let newRefreshToken: string | null = null
    if (canDelegate) {
      const { access_token } = await requestJwtFromNative()
      newToken = access_token
    } else {
      if (!doRefreshToken) {
        throw new Error('No refresh token available')
      }

      const response = isBackOfficeTokenMode
        ? await refreshToken()
        : await refreshTokenB2C()

      newToken = isBackOfficeTokenMode
        ? response.result.access_token
        : response.token
      newRefreshToken = isBackOfficeTokenMode
        ? response.result.refresh_token
        : response.refreshToken

      if (!newRefreshToken && !config.identity.isWebView) {
        throw new Error('Did not receive a new refresh token')
      }
    }

    if (newToken) {
      const size = byteLength(newToken)
      if (size > MAX_TOKEN_BYTES) {
        throw new Error(
          `Access token size ${size} bytes exceeds limit ${MAX_TOKEN_BYTES} bytes`
        )
      }
      setCookie(
        isBackOfficeTokenMode ? vars.b2b_access_token : accessTokenCookie,
        newToken
      )
    }

    if (newRefreshToken && !canDelegate) {
      setCookie(
        isBackOfficeTokenMode ? vars.b2b_refresh_token : refreshTokenCookie,
        newRefreshToken
      )
    }

    processQueue(null, newToken, newRefreshToken)
    return { token: newToken, refreshToken: newRefreshToken }
  } catch (error) {
    console.error('Token refresh failed:', error)
    processQueue(error as Error, null, null)
    logout()
    return null
  } finally {
    isRefreshing = false
    _refreshPromise = null
  }
}

export const getRefreshedToken = (
  doRefreshToken: string | boolean | null
): Promise<{ token: string | null; refreshToken: string | null } | null> => {
  if (!isRefreshing) {
    isRefreshing = true
    _refreshPromise = performTokenRefresh(doRefreshToken)
  }
  return new Promise((resolve, reject) => {
    retryQueue.push({ resolve, reject })
  })
}
